/**
 * Team Entitlement Resolver
 *
 * SINGLE ENTRY POINT for all plan/subscription logic.
 * CANONICAL ONLY - does NOT fall back to legacy fields.
 *
 * If entitlement is missing, returns an invalid state that
 * the UI should handle by showing "Create new team" message.
 *
 * Usage:
 *   const entitlement = resolveTeamEntitlement(teamDoc);
 *   if (entitlement.isInvalid) { showCreateNewTeamMessage(); }
 *   if (entitlement.hasFullAccess) { ... }
 *   if (entitlement.isFree) { ... }
 */

import type { Timestamp } from 'firebase/firestore';
import type {
  TeamWithEntitlement,
  ResolvedEntitlement,
  PlanTier,
  PlanId,
  AccessStatus,
  EntitlementState,
  CancellationStatus,
} from './types';

// ============================================================================
// Timestamp Helpers
// ============================================================================

/**
 * Convert Firestore Timestamp to Date safely
 */
function toDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  return null;
}

// ============================================================================
// Main Resolver
// ============================================================================

/**
 * Resolve team entitlement from team document.
 *
 * CANONICAL ONLY - requires team.entitlement.tier to be present.
 * If missing, returns an invalid entitlement that the UI should handle.
 *
 * @param team - Team document with entitlement object
 * @returns Resolved entitlement with all computed properties
 */
export function resolveTeamEntitlement(
  team: TeamWithEntitlement | null | undefined
): ResolvedEntitlement {
  // Missing team → invalid state
  if (!team) {
    return createInvalidEntitlement('Team document is missing');
  }

  // Extended team type to access root-level entitlementSchemaVersion
  const teamDoc = team as TeamWithEntitlement & {
    id?: string;
    entitlementSchemaVersion?: number;
  };

  // Check for canonical entitlement object
  if (teamDoc.entitlement?.tier) {
    return resolveFromCanonical(teamDoc.entitlement);
  }

  // Check for root-level entitlementSchemaVersion as canonical marker
  const hasCanonicalMarker =
    teamDoc.entitlementSchemaVersion !== undefined ||
    teamDoc.entitlement?.schemaVersion !== undefined;

  if (hasCanonicalMarker && teamDoc.entitlement) {
    return resolveFromCanonical(teamDoc.entitlement);
  }

  // No canonical entitlement → return invalid state
  // UI should show "Create new team" message
  return createInvalidEntitlement('Team is missing entitlement data. Please create a new team.');
}

// ============================================================================
// Canonical Resolution
// ============================================================================

// ============================================================================
// Legacy state → AccessStatus mapping
// ============================================================================

/**
 * Map a legacy EntitlementState to the canonical AccessStatus.
 * Used when a Firestore doc has `state` but no `accessStatus`.
 *
 * Mapping:
 *   free     → 'free'
 *   trialing → 'active'   (trial = paid access)
 *   active   → 'active'
 *   canceled → 'active'   (still within paid period)
 *   past_due → 'active'   (grace period, still has access)
 *   expired  → 'restricted'
 *   (unknown)→ 'restricted'
 */
function legacyStateToAccessStatus(
  tier: PlanTier,
  state: EntitlementState | undefined
): AccessStatus {
  if (tier === 'free') return 'free';
  switch (state) {
    case 'trialing':
    case 'active':
    case 'canceled':
    case 'past_due':
      return 'active';
    case 'free':
      return 'free';
    case 'expired':
    default:
      return 'restricted';
  }
}

/**
 * Map a legacy LegacyPlanTier to the canonical PlanTier.
 * 'free' stays 'free'; anything else becomes 'paid'.
 */
function legacyTierToTier(rawTier: string | undefined): PlanTier {
  if (!rawTier || rawTier === 'free') return 'free';
  return 'paid';
}

/**
 * Derive PlanId from tier and optional legacy tier string.
 */
function derivePlanId(tier: PlanTier, rawTier?: string): PlanId {
  if (tier === 'free') return 'free';
  // Future: map rawTier 'starter'→'paid_starter', etc. when those PlanId variants exist
  return 'paid_default';
}

/**
 * Map AccessStatus back to a legacy EntitlementState for backward compat.
 */
function accessStatusToLegacyState(accessStatus: AccessStatus): EntitlementState {
  switch (accessStatus) {
    case 'free':
      return 'free';
    case 'active':
      return 'active';
    case 'restricted':
      return 'expired';
  }
}

// ============================================================================
// Canonical Resolution
// ============================================================================

/**
 * Resolve from canonical entitlement object
 */
function resolveFromCanonical(
  entitlement: NonNullable<TeamWithEntitlement['entitlement']>
): ResolvedEntitlement {
  // Normalise tier: accept legacy multi-tier values from existing Firestore docs
  const rawTier = entitlement.tier as string;
  const tier: PlanTier = legacyTierToTier(rawTier);

  // Resolve accessStatus: prefer explicit field, fall back to legacy state mapping
  const accessStatus: AccessStatus =
    entitlement.accessStatus ?? legacyStateToAccessStatus(tier, entitlement.state);

  const planId: PlanId = entitlement.planId ?? derivePlanId(tier, rawTier);

  const activatedAt = toDate(entitlement.activatedAt);
  const source = entitlement.source;

  // Resolve trial dates
  let trial: ResolvedEntitlement['trial'] | undefined;
  if (entitlement.trial) {
    const startedAt = toDate(entitlement.trial.startedAt);
    const endsAt = toDate(entitlement.trial.endsAt);
    if (startedAt && endsAt) {
      trial = { startedAt, endsAt };
    }
  }

  // Resolve cancellation fields
  const cancelStatus = entitlement.cancelStatus;
  const cancelEffectiveAt = toDate(entitlement.cancelEffectiveAt);
  const isDeactivated = cancelStatus === 'effective' || !!entitlement.deactivatedAt;

  // Canonical hasFullAccess formula (contract §4.4)
  const hasFullAccess = computeHasFullAccess(tier, accessStatus, isDeactivated);
  const isRestricted = accessStatus === 'restricted' || isDeactivated;

  return {
    tier,
    planId,
    accessStatus,
    state: accessStatusToLegacyState(accessStatus),
    activatedAt,
    source,
    trial,
    hasFullAccess,
    isFree: tier === 'free',
    isPaid: tier === 'paid',
    isRestricted,
    isInvalid: false,
    cancelStatus,
    cancelEffectiveAt: cancelEffectiveAt ?? undefined,
    isDeactivated,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Create invalid entitlement for teams missing canonical data
 */
function createInvalidEntitlement(reason: string): ResolvedEntitlement {
  return {
    tier: 'free',
    planId: 'free',
    accessStatus: 'free',
    state: 'expired',
    activatedAt: null,
    source: 'signup',
    trial: undefined,
    hasFullAccess: false,
    isFree: true,
    isPaid: false,
    isRestricted: false,
    isInvalid: true,
    invalidReason: reason,
    isDeactivated: false,
  };
}

/**
 * Compute whether team has full paid access.
 *
 * Canonical formula (contract §4.4):
 *   hasFullAccess = (tier === 'paid') && (accessStatus === 'active') && (!isDeactivated)
 */
function computeHasFullAccess(
  tier: PlanTier,
  accessStatus: AccessStatus,
  isDeactivated: boolean = false
): boolean {
  if (isDeactivated) return false;
  if (tier === 'free') return false;
  return accessStatus === 'active';
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Check if team has full access (paid features)
 */
export function hasFullAccess(team: TeamWithEntitlement | null | undefined): boolean {
  return resolveTeamEntitlement(team).hasFullAccess;
}

/**
 * Check if team is on free tier
 */
export function isFreeTeam(team: TeamWithEntitlement | null | undefined): boolean {
  return resolveTeamEntitlement(team).isFree;
}

/**
 * Check if team is currently trialing
 */
export function isTrialing(team: TeamWithEntitlement | null | undefined): boolean {
  return resolveTeamEntitlement(team).state === 'trialing';
}

/**
 * Get the effective plan tier
 */
export function getEffectiveTier(team: TeamWithEntitlement | null | undefined): PlanTier {
  return resolveTeamEntitlement(team).tier;
}

/**
 * Get the entitlement state
 */
export function getEntitlementState(
  team: TeamWithEntitlement | null | undefined
): EntitlementState {
  return resolveTeamEntitlement(team).state;
}

/**
 * Check if team has a scheduled cancellation
 */
export function hasCancellationScheduled(team: TeamWithEntitlement | null | undefined): boolean {
  return resolveTeamEntitlement(team).cancelStatus === 'scheduled';
}

/**
 * Check if team is deactivated (after cancellation effective date)
 */
export function isTeamDeactivated(team: TeamWithEntitlement | null | undefined): boolean {
  return resolveTeamEntitlement(team).isDeactivated;
}

// ============================================================================
// Canonical Deactivation Helper
// ============================================================================

/**
 * Resolve team deactivation from a raw Firestore team document.
 *
 * Detection order (binding — Team Deactivation & Access Gating Contract V1.0 §2):
 *   1. PRIMARY:  `teams/{teamId}.isActive === false`
 *   2. FALLBACK: `teams/{teamId}.entitlement.cancelStatus === 'effective'`
 *   3. FALLBACK: `teams/{teamId}.entitlement.deactivatedAt` is set
 *   4. FALLBACK: `teams/{teamId}.status === 'deactivated'`  (legacy field)
 *
 * Returns true if ANY condition is met.
 * Returns false if team doc is null/undefined (caller must handle missing doc separately).
 *
 * This is the SINGLE entry point for deactivation detection in the UI.
 * TeamContext calls this; no other code should derive isDeactivated independently.
 */
export function resolveTeamDeactivation(
  teamDoc: Record<string, unknown> | null | undefined
): boolean {
  if (!teamDoc) return false;

  // 1. PRIMARY: isActive field (canonical — set by Cloud Function on deactivation)
  if (teamDoc.isActive === false) return true;

  // 2-3. FALLBACK: entitlement sub-object fields (backward compat)
  const entitlement = teamDoc.entitlement as Record<string, unknown> | undefined;
  if (entitlement) {
    if (entitlement.cancelStatus === 'effective') return true;
    if (entitlement.deactivatedAt) return true;
  }

  // 4. FALLBACK: legacy root-level status field
  if (teamDoc.status === 'deactivated') return true;

  return false;
}
